import { useCallback, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  FormControlLabel,
  Switch
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import ApiTablePage from '../../components/common/ApiTablePage';
import ConfirmDeleteDialog from '../../components/common/ConfirmDeleteDialog';
import { API_CONFIG } from '../../config/api.config';
import useAPICall from '../../hooks/useAPICall';

const toolbarButtonSx = {
  ml: 2,
  px: 3,
  py: 1,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '10px',
  backgroundColor: '#1E88E5',
  boxShadow: '0 4px 12px rgba(30, 136, 229, 0.2)',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  '& .MuiButton-startIcon': { mr: 1 },
  '&:hover': {
    backgroundColor: '#1565C0',
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 16px rgba(30, 136, 229, 0.3)',
  },
  '&:active': { transform: 'translateY(0)' }
};

const modalTitleSx = {
  m: 0,
  p: 2,
  pb: 1.5,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: '1px solid #e5e7eb',
};

const MATCH_COLUMN_WIDTH = 160;

const isSystemGroup = (row) => (row?.groupName || row?.group_name || row?.name) === 'System';

const MatchRules = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { execute } = useAPICall();
  const [refreshKey, setRefreshKey] = useState(0);
  const entityType = location.pathname.includes('/configuration/drug/') ? 'drug' : 'hcp';
  const basePath = `/configuration/${entityType}`;

  // Duplicate Dialog State
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateSourceRow, setDuplicateSourceRow] = useState(null);
  const [duplicateData, setDuplicateData] = useState({
    groupName: '',
    groupDescription: '',
    defaultGroup: false,
  });

  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    target: null, // { type: 'group' | 'rule', payload: any }
  });

  const onDeleteClick = useCallback((row) => {
    setDeleteConfirm({ open: true, target: { type: 'group', payload: row } });
  }, []);


  const confirmDelete = useCallback(async () => {
    if (!deleteConfirm.target) return;

    if (deleteConfirm.target.type === 'group') {
      const row = deleteConfirm.target.payload;
      const groupName = row.groupName || row.group_name || row.name;
      if (groupName) {
        try {
          await execute(
            { endpoint: '/rule/ruleConfig/matchRule/:groupName', method: 'DELETE' },
            null,
            {
              params: { groupName: encodeURIComponent(groupName) },
              successMessage: 'Match rule group deleted',
              showSuccessToast: true,
              errorMessage: 'Unable to delete match rule group.',
            }
          );
          setRefreshKey((prev) => prev + 1);
        } catch {
        }
      }
    } else if (deleteConfirm.target.type === 'rule') {
      const index = deleteConfirm.target.payload;
      const newRules = [...duplicateData.matchRules];
      newRules.splice(index, 1);
      setDuplicateData({ ...duplicateData, matchRules: newRules });
    }

    setDeleteConfirm({ open: false, target: null });
  }, [deleteConfirm.target, duplicateData, execute]);

  const onDuplicateClick = useCallback((row) => {
    setDuplicateSourceRow(row);
    setDuplicateData({
      groupName: `${row.groupName || row.group_name || row.name}_Copy`,
      groupDescription: row.groupDescription || row.group_description || row.description || '',
      defaultGroup: false,
      matchRules: JSON.parse(JSON.stringify(row.matchRules || row.rules || [])),
    });
    setDuplicateModalOpen(true);
  }, []);

  const handleDuplicateSave = useCallback(async () => {
    if (!duplicateData.groupName) {
      return;
    }

    try {
      const duplicatedRuleGroup = {
        ...duplicateSourceRow,
        groupName: duplicateData.groupName,
        groupDescription: duplicateData.groupDescription,
        defaultGroup: duplicateData.defaultGroup,
        matchRules: duplicateData.matchRules,
      };

      await execute(
        { endpoint: '/rule/ruleConfig/matchRule', method: 'POST' },
        { matchRuleGroups: [duplicatedRuleGroup] },
        {
          successMessage: 'New group created successfully',
          showSuccessToast: true,
          errorMessage: 'Failed to duplicate match rule group',
        }
      );

      setDuplicateModalOpen(false);
      setRefreshKey((prev) => prev + 1);
    } catch {
    }
  }, [duplicateData, duplicateSourceRow, execute]);

  const columns = useMemo(() => ([
    { label: 'Name', keys: ['groupName', 'group_name', 'name'], size: MATCH_COLUMN_WIDTH },
    { label: 'Description', keys: ['groupDescription', 'group_description', 'description'], size: MATCH_COLUMN_WIDTH },
    { label: 'Default', keys: ['defaultGroup', 'default'], align: 'center', size: MATCH_COLUMN_WIDTH },
    {
      label: 'Number of Match Rules',
      render: (row) => row?.matchRules?.length ?? row?.rules?.length ?? 0,
      align: 'center',
      size: MATCH_COLUMN_WIDTH,
    },
    {
      label: 'Action',
      align: 'center',
      size: MATCH_COLUMN_WIDTH,
      cell: ({ row }) => {
        const original = row.original;
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
            <Tooltip title="Duplicate">
              <IconButton size="small" onClick={() => onDuplicateClick(original)}>
                <ContentCopyIcon fontSize="small" sx={{ color: '#f59e0b' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                color="primary"
                onClick={() => navigate(`${basePath}/edit-match-rules`, { state: { row: original } })}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" color="error" onClick={() => onDeleteClick(original)} disabled={isSystemGroup(original)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ]), [basePath, navigate, onDeleteClick, onDuplicateClick]);

  const renderRulesTable = () => {
    const rules = duplicateData?.matchRules || [];
    if (!rules || rules.length === 0) return null;
    return (
      <Box sx={{ mt: 0 }}>
        <Typography sx={{ fontWeight: 600, color: '#475569', mb: 1, fontSize: '0.875rem' }}>
          Match Rules
        </Typography>
        <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead style={{ backgroundColor: '#f8fafc', position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#475569' }}>Label</th>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#475569' }}>Name</th>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#475569' }}>Type</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule, idx) => (
                <tr key={idx} style={{ borderBottom: idx === rules.length - 1 ? 'none' : '1px solid #e5e7eb' }}>
                  <td style={{ padding: '8px', color: '#1e293b' }}>{rule.label || rule.ruleLabel || rule.description || '-'}</td>
                  <td style={{ padding: '8px', color: '#1e293b' }}>{rule.name || rule.ruleName || '-'}</td>
                  <td style={{ padding: '8px', color: '#1e293b' }}>{rule.matchAction || rule.action || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <ApiTablePage
        key={refreshKey}
        title="Match Rules"
        subtitle={`${entityType.toUpperCase()} matching rule configuration`}
        apiConfig={API_CONFIG.config.getMatchRules}
        columns={columns}
        toolbarActions={(
        <Button className="action-btn process-btn action-btn process-btn"
          variant="contained"
          onClick={() => navigate(`${basePath}/create-match-rules`)}
          sx={toolbarButtonSx}
            startIcon={<AddIcon />}
        >
          Match Rules
        </Button>
      )}
        onRowClick={(row) => navigate(`${basePath}/edit-match-rules`, { state: { row: row.original } })}
        muiTableBodyRowProps={({ row }) => ({
          onDoubleClick: () => navigate(`${basePath}/edit-match-rules`, { state: { row: row.original } }),
          sx: { cursor: 'pointer' },
        })}
      />

      {/* Duplicate Modal */}
      <Dialog
        open={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
      >
        <DialogTitle sx={modalTitleSx}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
            Duplicate Match Rule
          </Typography>
          <IconButton size="small" onClick={() => setDuplicateModalOpen(false)} sx={{ color: '#64748b' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box>
              <Typography sx={{ fontWeight: 600, color: '#475569', mb: 1, fontSize: '0.875rem' }}>
                Rule Name <Box component="span" sx={{ color: '#ef4444' }}>*</Box>
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={duplicateData.groupName}
                onChange={(e) => setDuplicateData({ ...duplicateData, groupName: e.target.value })}
                placeholder="Enter rule name"
                autoFocus
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "38px",
                  },
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    height: "38px",
                    boxSizing: "border-box",
                  },
                }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600, color: '#475569', mb: 1, fontSize: '0.875rem' }}>
                Rule Description <Box component="span" sx={{ color: '#ef4444' }}>*</Box>
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={duplicateData.groupDescription}
                onChange={(e) => setDuplicateData({ ...duplicateData, groupDescription: e.target.value })}
                placeholder="Enter rule description"
                sx={{
                "& .MuiOutlinedInput-root": {
                  height: "38px",
                },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  height: "38px",
                  boxSizing: "border-box",
                },
              }}
              />
            </Box>
            <Box>
              <FormControlLabel
                control={(
                  <Switch
                    checked={duplicateData.defaultGroup}
                    onChange={(e) => setDuplicateData({ ...duplicateData, defaultGroup: e.target.checked })}
                    color="primary"
                  />
                )}
                label={(
                  <Typography sx={{ color: '#475569', fontSize: '0.875rem' }}>
                    Active
                  </Typography>
                )}
              />
            </Box>
            {renderRulesTable()}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={() => setDuplicateModalOpen(false)}
            variant="outlined"
            sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDuplicateSave}
            variant="contained"
            disabled={!duplicateData.groupName || !duplicateData.groupDescription}
            sx={{ textTransform: 'none', fontWeight: 600, px: 3, boxShadow: 'none' }}
          >
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDeleteDialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, target: null })}
        onConfirm={confirmDelete}
        itemName={deleteConfirm.target?.type === 'group' ? 'match rule group' : 'rule'}
      />
    </>
  );
};

export default MatchRules;
